// url-secret-scrub: keep secrets that arrive in the URL out of document.location
// and document.referrer before any tracking script can read them.
//
// Two halves that MUST stay in sync:
//   1. The inline <script id="url-secret-scrub"> at the top of public/index.html
//      runs before the GL tag guard, GTM and the Facebook pixel boot. On
//      /socialAuth and /reset-password it moves the secret query keys into
//      sessionStorage (STASH_PREFIX + pathname) and rewrites the URL with
//      history.replaceState. It fails open: if the stash cannot be written the
//      URL is left alone so sign-in and password reset keep working. A
//      /socialAuth landing with no token in the URL (a cancelled or failed
//      Google sign-in) clears that route's stash instead, so an earlier
//      landing's unused token is never read as this one's.
//   2. This module is what the React pages read from: the stash first, then the
//      query as a fallback for the case where the head script could not run or
//      could not stash. In the fallback case it removes the keys from the query
//      itself, so the page never navigates or hands the secret on while the
//      secret is still in the URL.
//
// Why (F1, 2026-09-18): the Google sign-in return URL, /socialAuth?token=<JWT>,
// became document.referrer for the whole SPA session and was sent to GA4,
// Google Ads, the Facebook pixel, Mixpanel and Hotjar on every sign-in.
//
// The quiz-link claim token (bottom of this file) is the third secret the head
// script handles. It rides in the PATH, so it follows its own rules there.
export const STASH_PREFIX = 'qlearn_url_secret_stash:';

export const SECRET_QUERY_KEYS = Object.freeze({
  '/socialAuth': ['token'],
  '/reset-password': ['token', 'email'],
});

const safeSessionStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

export const stashKeyFor = (pathname) => STASH_PREFIX + pathname;

// Reads and clears the stash written by the head script. Single-use: a second
// read returns {}. Returns {} for anything unreadable rather than throwing.
export const takeStashedSecrets = (pathname, storage = safeSessionStorage()) => {
  if (!storage) return {};
  const key = stashKeyFor(pathname);
  let raw = null;
  try {
    raw = storage.getItem(key);
  } catch (e) {
    return {};
  }
  if (raw == null) return {};
  try {
    storage.removeItem(key);
  } catch (e) {
    // Best effort; the value is still returned below.
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

// Removes `keys` from the current document URL without navigating, preserving
// every other query parameter, the hash and history.state. Returns the removed
// values ({} when none were present). Never throws; if replaceState fails the
// values are still returned and the URL is left as is.
export const scrubSecretsFromLocation = (
  keys,
  { location = window.location, history = window.history } = {}
) => {
  const removed = {};
  try {
    const current = new URL(location.href);
    let changed = false;
    keys.forEach((key) => {
      if (current.searchParams.has(key)) {
        removed[key] = current.searchParams.get(key);
        current.searchParams.delete(key);
        changed = true;
      }
    });
    if (!changed) return removed;
    const clean = current.pathname + (current.search || '') + (current.hash || '');
    history.replaceState(history.state, '', clean);
  } catch (e) {
    // Leave the URL untouched; the caller still gets the values it needs.
  }
  return removed;
};

// Resolves the secrets for the current document. The stash (written by the
// head script) wins; the query is the fallback and is scrubbed either way.
// Returns { values: { [key]: string | null }, source: 'stash' | 'query' | 'none' }.
export const readUrlSecrets = (
  pathname,
  keys = SECRET_QUERY_KEYS[pathname] || [],
  deps = {}
) => {
  const stashed = takeStashedSecrets(pathname, deps.storage);
  const fromQuery = scrubSecretsFromLocation(keys, deps);
  const values = {};
  let source = 'none';
  keys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(stashed, key) && stashed[key] != null) {
      values[key] = String(stashed[key]);
      source = 'stash';
    } else if (Object.prototype.hasOwnProperty.call(fromQuery, key)) {
      values[key] = fromQuery[key];
      if (source === 'none') source = 'query';
    } else {
      values[key] = null;
    }
  });
  return { values, source };
};

// ── Quiz-link claim token ───────────────────────────────────────────────────
//
// A post-session quiz email links to /notes-to-quiz/<claim token>, and a
// signed-out visitor used to be sent on to /login?next=%2Fnotes-to-quiz%2F<token>.
// GTM (GA4 with it) and the Meta pixel read the page address, and GTM reports
// every history change, so both forms handed the token to third parties.
//
// A path segment cannot be deleted the way a query key can: the router needs
// something there. So the token is kept in sessionStorage for the tab and the
// address uses the fixed segment below instead. /notes-to-quiz/claim matches
// the same route (/notes-to-quiz/:token) and sits under the same prefix every
// route check in the app already uses, so nothing else has to know about it.
//
// Differences from the query secrets above, all deliberate:
//   - Not single-use. A reload, Back, Forward and the return from sign-in all
//     land on the token-free address and must read the token again.
//   - One slot per tab: a newer link opened in the same tab replaces the older.
//   - Never written to history.state (GTM exposes it to its tags) and never to
//     localStorage.
//
// The head script does this on every document load, and public/index.html
// holds GTM and the pixel back while the address still carries a token (when
// the stash could not be written, so the address was left for the claim to
// keep working). The functions here are what the React side uses so that an
// in-app navigation never puts the token back. Keep the pattern, the segment
// and the stash key in sync with the head script;
// src/seo/url-secret-scrub-shell.test.js checks both against each other.
const QUIZ_LINK_OPAQUE_SEGMENT = 'claim';
export const QUIZ_LINK_OPAQUE_PATH = `/notes-to-quiz/${QUIZ_LINK_OPAQUE_SEGMENT}`;
export const QUIZ_LINK_STASH_KEY = `${STASH_PREFIX}/notes-to-quiz`;

// [1] the route prefix as written, [2] the segment after it, [3] the rest.
// Case-insensitive because React Router v5 matches routes that way.
const QUIZ_LINK_PATH_PATTERN = /^(\/notes-to-quiz\/)([^/?#]+)(.*)$/i;

const isOpaqueSegment = (segment) =>
  typeof segment === 'string' && segment.toLowerCase() === QUIZ_LINK_OPAQUE_SEGMENT;

// One reading of a path segment as a token, used everywhere, so the token the
// claim page uses and the one that was stashed can never differ.
const tokenFromSegment = (segment) => {
  try {
    return decodeURIComponent(segment);
  } catch (e) {
    return segment; // not valid percent-encoding: keep it as it was written
  }
};

// The tab stash is what survives a new document. This copy covers the one case
// it cannot: storage refusing the write. An in-app navigation stays in this
// document, so it can always use the token-free path and still find the token.
let tokenInThisDocument = null;

// Test seam only: what a new document does to this module. Production code
// never calls this.
export const __resetQuizLinkTokenMemory = () => {
  tokenInThisDocument = null;
};

export const rememberQuizLinkToken = (token, storage = safeSessionStorage()) => {
  if (typeof token !== 'string' || token === '') return;
  tokenInThisDocument = token;
  try {
    storage.setItem(QUIZ_LINK_STASH_KEY, JSON.stringify({ token }));
  } catch (e) {
    // Storage blocked or missing: the copy above still serves this document.
  }
};

// Reads the token without clearing it. Null when there is none to read. This
// document's own copy wins: it is only ever set after the head script ran, so
// it is never older than the stash.
export const readQuizLinkToken = (storage = safeSessionStorage()) => {
  if (tokenInThisDocument !== null) return tokenInThisDocument;
  try {
    const stashed = JSON.parse(storage.getItem(QUIZ_LINK_STASH_KEY))?.token;
    return typeof stashed === 'string' && stashed !== '' ? stashed : null;
  } catch (e) {
    return null; // unreadable or not JSON
  }
};

// The token-free form of a quiz-link path. A path that carries a token has it
// remembered and replaced by the fixed segment; whatever followed the token
// (a trailing slash, a query, a fragment) is kept. Anything else, a path that
// is already token-free included, comes back unchanged.
export const toOpaqueQuizLinkPath = (path, storage = safeSessionStorage()) => {
  if (typeof path !== 'string') return path;
  const match = QUIZ_LINK_PATH_PATTERN.exec(path);
  if (!match || isOpaqueSegment(match[2])) return path;
  rememberQuizLinkToken(tokenFromSegment(match[2]), storage);
  return `${match[1]}${QUIZ_LINK_OPAQUE_SEGMENT}${match[3]}`;
};

// The sign-in page's own fallback for the head script: if ?next= still carries
// a token when the page mounts (the head script could not store it), remember
// it and rewrite that one value in place, so the address is token-free before
// the page fires any event. Mixpanel is bundled, not started by the tag gate,
// and attaches the page address to every event. Returns true when it rewrote.
export const scrubQuizLinkNextFromLocation = ({
  location = window.location,
  history = window.history,
  storage = safeSessionStorage(),
} = {}) => {
  try {
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    const tokenFree = toOpaqueQuizLinkPath(next, storage);
    if (tokenFree === next) return false;
    params.set('next', tokenFree);
    history.replaceState(history.state, '', `${location.pathname}?${params.toString()}${location.hash || ''}`);
    return true;
  } catch (e) {
    return false; // leave the address; the caller still navigates token-free
  }
};

// The claim token for the claim page, given its route segment: the fixed
// segment means "read the stash"; any other segment is the token itself (the
// head script could not move it out of the address). Null when there is none.
export const resolveQuizLinkToken = (routeSegment, storage = safeSessionStorage()) => {
  if (typeof routeSegment !== 'string' || routeSegment === '') return null;
  return isOpaqueSegment(routeSegment) ? readQuizLinkToken(storage) : tokenFromSegment(routeSegment);
};
